'use client';

/**
 * MobilePurchaseFlow Component
 *
 * Standalone wrapper for PurchaseFlow that handles mobile app communication.
 * This component posts messages directly to the mobile app, reading most
 * data from URL params. Redux is used only for affiliate promo slots
 * (user bonus info needed by TopBanner).
 *
 * Usage:
 * - Render this component in a mobile-specific route
 * - Pass props directly or let it read from URL params
 *
 * URL params supported:
 * - price: Amount to charge (final amount to show and pay)
 * - credits_requested: Credit amount the user receives (when absent, CC is delegated to native app)
 * - bonus: Bonus amount
 * - purchase_type: Type of purchase
 * - advisor_id: Advisor ID for chat context
 * - payg: Whether this is a pay-as-you-go session (true/false)
 * - click_source: Analytics click source
 * - duration: Session duration (informational)
 * - context: Purchase context (e.g., 'live_chat', 'video_call')
 * - purchase_screen: Analytics purchase screen name
 * - intended_advisor_id: Advisor ID override for metadata
 *
 * Message patterns (sent to app):
 * - initial_loaded: Payment options data loaded (component ready)
 * - on_pay_completed: Payment successful (with okMessage, cashback)
 * - dismiss_options: Enable/disable native dismiss controls during processing
 * - initial_option_picked: User started a purchase action
 * - initiate_session: PAYG validation session starting
 * - skip_and_continue: User dismissed additionalFunds flow
 * - navigation_update: Navigate to specific route (e.g., spending_limit)
 * - dismiss: Close the purchase flow
 */

import React, {
  useCallback, useMemo, useEffect, useState, useRef
} from 'react';
import { useSelector } from 'react-redux';
import { isAndroid } from 'react-device-detect';
import queryString from 'query-string';
import type {
  SupportedCurrency,
  CloseReason, CompletionResult, MobileAppProps, FlowMode, ScreenChangeInfo,
  ConversationType, PurchaseFlowConfig,
  PurchaseType
} from '../types';
import PurchaseFlow from '../PurchaseFlow';
import { toPurchaseFlowTheme } from '../types';
import {
  sendInitialLoaded,
  sendPayCompletedWithResult,
  sendDismiss,
  sendDismissOptions,
  sendInitialOptionPicked,
  sendSkipAndContinue,
  sendInitiateSession,
  sendNavigationUpdate,
  sendAnalytics,
  sendCrossPlatformNavigation,
  sendPaymentMethodAdded,
  messageToAppKeys
} from './mobileMessaging';
import { useAffiliatePromoSlots } from '../hooks';
import { isWebProcessedPayment } from '../gateways/componentSelector';
import { withCaptcha, getAppTheme } from '../../../../common/config/utils';
import { purchaseFlowOnLog } from '../../../../common/config/purchaseFlowRenderHelpers';
import classes from './classes.module.scss';

/** Safe JSON parse — returns parsed object or false on failure */
const tryParseJSONObject = (jsonString: string): Record<string, any> | false => {
  try {
    const o = JSON.parse(jsonString);
    if (o && typeof o === 'object') return o;
  } catch { /* ignore */ }
  return false;
};

// ============================================================================
// Constants
// ============================================================================

// ============================================================================
// Types
// ============================================================================

interface MobilePurchaseFlowProps {
  /** PurchaseFlow configuration (rendering, headers, analytics, etc.) */
  config: PurchaseFlowConfig;
  /** Anonymous user bonus infos — KA-specific, not present in POWeb Redux store */
  anonymousUserBonusInfos?: any[];
}

// ============================================================================
// URL Parameter Mapping
// ============================================================================

/** Session-related purchase types that imply "additional funds" mode */
const SESSION_PURCHASE_TYPES = new Set(['live_chat', 'voice_call', 'video_call']);

/**
 * Derive the FlowMode from the raw purchase_type URL parameter.
 * - Session types (live_chat, voice_call, video_call) → additionalFunds
 * - matchup_promo → affiliatePromo
 * - Everything else (side_menu, add_funds, …) → purchase
 */
const resolveFlowMode = (rawPurchaseType?: string): FlowMode => {
  if (!rawPurchaseType) return 'purchase';
  if (SESSION_PURCHASE_TYPES.has(rawPurchaseType)) return 'additionalFunds';
  if (rawPurchaseType === 'matchup_promo') return 'affiliatePromo';
  return 'purchase';
};

/**
 * Derive ConversationType from the `context` URL param.
 * Matches old flow's contextToChatType → PurchaseFlowWrapper's getConversationType chain.
 */
const resolveConversationType = (context?: string): ConversationType | undefined => {
  switch (context) {
    case 'live_chat': return 'chat';
    case 'voice_call': return 'voice';
    case 'video_call': return 'video';
    default: return undefined;
  }
};

/**
 * Map URL purchase_type param to the API format.
 * Matches old flow's getPurchaseType (purchaseTypes map → purchaseDetailsTypes constant → toLowerCase).
 * URL values use a different naming convention than what the API expects.
 */
const URL_PURCHASE_TYPE_TO_API: Record<string, PurchaseType> = {
  live_chat: 'chat',
  voice_call: 'voice_chat',
  video_call: 'video_chat',
  matchup_promo: 'affiliate_promo_popup',
  side_menu: 'side_menu',
  holistic_service: 'holistic_service'
};

const resolvePurchaseType = (rawPurchaseType?: string): PurchaseType | undefined => (
  rawPurchaseType ? URL_PURCHASE_TYPE_TO_API[rawPurchaseType] : undefined
);

/** Matches old flow's getClickSource: replaces underscores with spaces */
const normalizeClickSource = (clickSource?: string): string | undefined => clickSource?.split('_').join(' ');

// ============================================================================
// URL Parameter Parsing
// ============================================================================

const parseUrlParams = () => {
  if (typeof window === 'undefined') return {};

  const params = queryString.parse(window.location.search);

  return {
    price: params.price ? Number(params.price) : undefined,
    creditsRequested: params.credits_requested ? Number(params.credits_requested) : undefined,
    bonus: params.bonus ? Number(params.bonus) : undefined,
    purchaseType: resolvePurchaseType(params.purchase_type as string),
    mode: resolveFlowMode(params.purchase_type as string),
    isPayg: params.payg === 'true',
    clickSource: normalizeClickSource(params.click_source as string),
    duration: params.duration as string | undefined,
    context: params.context as string | undefined,
    purchaseScreen: params.purchase_screen as string | undefined,
    advisorId: params.intended_advisor_id ? Number(params.intended_advisor_id) : undefined,
    bottomPadding: params.bottom_padding ? parseFloat(params.bottom_padding as string) || 0 : 0
  };
};

// ============================================================================
// Hook for receiving messages from mobile app
// ============================================================================

const useReceivedMessageFromApp = () => {
  const [receivedMessage, setReceivedMessage] = useState<Record<string, any> | undefined>();

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      const parsedMessage = tryParseJSONObject(e.data);
      if (parsedMessage) {
        setReceivedMessage(parsedMessage);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const clearReceivedMessage = useCallback(() => {
    setReceivedMessage(undefined);
  }, []);

  return { receivedMessage, clearReceivedMessage };
};

interface MobilePurchaseFlowInnerProps extends MobilePurchaseFlowProps {
  dynamicConfig: any;
}

const MobilePurchaseFlowInner: React.FC<MobilePurchaseFlowInnerProps> = ({
  config: incomingConfig,
  anonymousUserBonusInfos,
  dynamicConfig
}) => {
  const handleSpendingLimitAction = useCallback(() => {
    sendNavigationUpdate('spending_limit');
    sendDismiss();
  }, []);

  // Parse URL params
  const urlParams = useMemo(() => parseUrlParams(), []);

  // Use URL params for purchase configuration
  const {
    isPayg, mode = 'purchase', purchaseType, advisorId, clickSource, context, duration, purchaseScreen
  } = urlParams;
  const metadata = useMemo(() => ({
    advisorId,
    clickSource,
    purchaseScreen,
    conversationType: resolveConversationType(context),
    ...(context ? { context } : {}),
    ...(duration ? { duration: parseFloat(duration) } : {})
  }), [advisorId, clickSource, purchaseScreen, context, duration]);

  // Track selectedPaymentKind from screen changes for GP okMessage suppression
  const selectedPaymentKindRef = useRef<string | undefined>(undefined);

  // Hook for receiving messages from app
  const { receivedMessage, clearReceivedMessage } = useReceivedMessageFromApp();

  // ============================================================================
  // Analytics Handler
  // ============================================================================

  // Stable ref for wrapper-provided onAnalyticsEvent to avoid recreating the handler
  const onAnalyticsEventRef = useRef(incomingConfig.onAnalyticsEvent);
  onAnalyticsEventRef.current = incomingConfig.onAnalyticsEvent;

  // Ref to capture Purchase event properties for on_pay_completed (sent to native).
  const purchaseAnalyticsRef = useRef<Record<string, any>>({});

  /**
   * Purchase events that are forwarded to native INSTEAD OF being tracked by web.
   * Matches old flow pattern (process_apple_pay.js, google_pay/index.js,
   * mobile_processing_status.js): on mobile, purchase analytics are sent to the
   * native app exclusively — the web does not track them.
   *
   * The old flow only needed this for AP/GP (CC was delegated to native via on_pay).
   * The new flow extends it to CC since CC is now web-processed too.
   */
  const NATIVE_ONLY_EVENTS = useMemo(() => new Set(['purchase started', 'purchase failed']), []);

  /**
   * Analytics handler: routes events either to native or web, never both.
   *
   * Purchase events (purchase started, purchase failed) → native only via sendAnalytics
   * Purchase completion → captured in purchaseAnalyticsRef, sent via on_pay_completed
   * All other events (payment method clicked, card added, etc.) → web analytics
   */
  const handleAnalyticsEvent = useCallback((
    eventName: string,
    properties: Record<string, any>,
    additionalProperties?: Record<string, any>
  ) => {
    if (NATIVE_ONLY_EVENTS.has(eventName)) {
      sendAnalytics({ [eventName]: properties });
      return;
    }

    if (eventName === 'Purchase') {
      purchaseAnalyticsRef.current = { [eventName]: properties };
      return;
    }

    onAnalyticsEventRef.current?.(eventName as any, properties, additionalProperties);
  }, [NATIVE_ONLY_EVENTS]);

  // ============================================================================
  // Cancel / Close handler (defined early so affiliate promo slots can reuse it)
  // ============================================================================

  const handleCancel = useCallback((reason?: CloseReason) => {
    if (reason === 'user_dismissed') {
      sendSkipAndContinue();
    } else {
      sendDismiss();
    }
  }, []);

  const handleMaybeLater = useCallback(
    () => handleCancel('user_dismissed'),
    [handleCancel]
  );

  // ============================================================================
  // Affiliate Promo Slots
  // ============================================================================

  const user = useSelector((state: any) => state.user?.user);

  const currencyKey: string = user?.currency ?? 'USD';
  const fxRate: number = dynamicConfig?.fxRate ?? 1;
  const supportedCurrencies: SupportedCurrency[] = dynamicConfig?.supportedCurrencies || [
    {
      key: 'USD',
      name: 'USD - US Dollar',
      sign: '$'
    }
  ];

  // For affiliate promo, the URL typically lacks price and has bonus=0.
  // The old flow derived these from the active offer (getActiveOffer), so we
  // fall back to offer data when URL params are missing or zero.
  const activeOffer = useMemo(() => {
    if (mode !== 'affiliatePromo') return null;
    return user?.bonusInfos?.[0] || anonymousUserBonusInfos?.[0] || null;
  }, [mode, user?.bonusInfos, anonymousUserBonusInfos]);

  const price = (activeOffer ? parseFloat(activeOffer.requiredPurchasesAmount) : urlParams.price) ?? 0;
  const bonus = (activeOffer ? parseFloat(activeOffer.bonus) : urlParams.bonus) ?? 0;
  const creditsRequested = (activeOffer ? price : urlParams.creditsRequested) ?? price + bonus;

  const affiliatePromoSlots = useAffiliatePromoSlots({
    isAffiliatePromo: mode === 'affiliatePromo',
    userBonusInfos: user?.bonusInfos,
    anonymousUserBonusInfos,
    userId: user?.id,
    userXfmProgram: user?.xfmProgram,
    onMaybeLater: handleMaybeLater,
    renderText: incomingConfig.renderText,
    locale: incomingConfig.locale || 'en'
  });

  // ============================================================================
  // PurchaseFlow Config (enriched with mobile analytics context)
  // ============================================================================

  const purchaseFlowConfig = useMemo(() => ({
    ...incomingConfig,
    theme: toPurchaseFlowTheme(getAppTheme()),
    onLog: purchaseFlowOnLog,
    analytics: {
      fromMobileApp: true,
      purchasesCount: user?.purchasesCount,
      advisorId
    },
    onAnalyticsEvent: handleAnalyticsEvent,
    withCaptcha
  }), [incomingConfig, handleAnalyticsEvent, user?.purchasesCount]);

  // ============================================================================
  // Mobile App Messages
  // ============================================================================

  const handleReady = useCallback(() => {
    setTimeout(() => { sendInitialLoaded(); }, 0);
  }, []);

  /**
   * Handle payment completion.
   *
   * For **web-processed** payments (AP, GP, CC on supported versions):
   *   - Sends `on_pay_completed` to native app with ok_message, cashback, and Purchase analytics
   *   - Matches old flow's handlePurchase in process_apple_pay.js / google_pay/index.js
   *
   * For **delegated** payments (PP, IAP, CC on old versions):
   *   - Does NOT send `on_pay_completed` — the native app already processed the payment
   *     and knows the result. Sending it again would be redundant.
   *   - Only sends `dismiss` to close the webview.
   *   - Matches old flow where web only sent `dismiss` after showing success UI.
   *
   * GP (Google Pay) okMessage is suppressed — the native app handles its own GP success UI.
   */
  const handleComplete = useCallback((result?: CompletionResult) => {
    // PAYG only validates the card — no actual payment occurs.
    // Send initiateSession so the native app starts the session (matching old flow).
    if (isPayg) {
      sendInitiateSession();
      return;
    }

    const paymentKind = selectedPaymentKindRef.current;
    const webProcessed = isWebProcessedPayment(paymentKind);

    // Only send on_pay_completed for web-processed payments.
    // Delegated payments (PP, IAP, CC old) are processed by the native app —
    // it already knows the result, so sending on_pay_completed would be redundant.
    if (webProcessed) {
      sendPayCompletedWithResult({
        // Prefer local ok message when available (shows amount in user's currency)
        okMessage: result?.okMessage,
        cashbackAmount: result?.cashbackAmount,
        analyticEvents: purchaseAnalyticsRef.current
      });
    }

    sendDismiss();
  }, [isPayg]);

  // ============================================================================
  // Rate Changed Handler (incoming message from native app)
  // ============================================================================

  useEffect(() => {
    if (!receivedMessage) return;
    if (receivedMessage.action === messageToAppKeys.rateChanged) {
      clearReceivedMessage();
      sendDismiss();
    }
  }, [receivedMessage, clearReceivedMessage]);

  /**
   * Handle payment failure.
   * Analytics (purchase failed) are forwarded to native via sendAnalytics in handleAnalyticsEvent.
   */
  const handleFailed = useCallback(() => {
    sendDismiss();
  }, []);

  // ============================================================================
  // Screen Change Handler
  // ============================================================================

  /**
   * Notify native app about screen transitions.
   * Sends cross_platform_navigation so the native toolbar can update
   * its title and back-button state (matches old PaymentMethodsListTitle behaviour).
   *
   * Also sends dismiss_options to lock/unlock native dismiss during processing,
   * and initial_option_picked when user starts payment with PayPal (old forMobileBuyBtnOnAddPayPalClicked, before on_pay).
   */
  const handleScreenChange = useCallback((info: ScreenChangeInfo) => {
    if (isAndroid) {
      sendCrossPlatformNavigation(info.title, info.showBackButton);
    }

    selectedPaymentKindRef.current = info.selectedPaymentKind;

    sendDismissOptions(info.isProcessing);
  }, []);

  // ============================================================================
  // Payment Method Added Handler
  // ============================================================================

  /**
   * Notify native app when a payment method is added.
   * Matches old flow behavior from add_credit_card.js:
   * sends { action: 'payment_method_added', params: { payment_methods_count: N } }
   */
  const handlePaymentMethodAdded = useCallback((paymentMethodsCount: number) => {
    sendPaymentMethodAdded(paymentMethodsCount);
  }, []);

  // ============================================================================
  // Mobile App Props
  // ============================================================================

  const mobileAppProps: MobileAppProps = useMemo(() => ({
    receivedMessageFromApp: receivedMessage,
    clearReceivedMessage
  }), [receivedMessage, clearReceivedMessage]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div
      className={ classes.root }
      style={ urlParams.bottomPadding ? { paddingBottom: urlParams.bottomPadding } : undefined }
    >
      <PurchaseFlow
        creditsRequested={ creditsRequested }
        price={ price }
        bonus={ bonus }
        fxRate={ fxRate }
        currencyKey={ currencyKey }
        supportedCurrencies={ supportedCurrencies }
        isPayg={ isPayg }
        purchaseType={ purchaseType }
        mode={ mode }
        metadata={ metadata }
        config={ purchaseFlowConfig }
        bannerSlot={ affiliatePromoSlots.bannerSlot }
        promoFooterSlot={ affiliatePromoSlots.promoFooterSlot }
        showCloseButton
        showSuccessScreen={ false }
        showErrorScreen
        onScreenChange={ handleScreenChange }
        onPaymentMethodAdded={ handlePaymentMethodAdded }
        onDelegatedPaymentStarted={ () => {
          sendInitialOptionPicked();
          sendDismissOptions(true);
        } }
        mobileAppProps={ mobileAppProps }
        onReady={ handleReady }
        onComplete={ handleComplete }
        onFailed={ handleFailed }
        onCancel={ handleCancel }
        onSpendingLimitAction={ handleSpendingLimitAction }
      />
    </div>
  );
};

// ============================================================================
// Component
// ============================================================================

export const MobilePurchaseFlow: React.FC<MobilePurchaseFlowProps> = (props) => {
  const dynamicConfig = useSelector((state: any) => state.application?.dynamicConfig);

  if (dynamicConfig && !dynamicConfig.supportedCurrencies?.length) {
    return null;
  }

  return <MobilePurchaseFlowInner { ...props } dynamicConfig={ dynamicConfig } />;
};

// ============================================================================
// Exports
// ============================================================================

export default MobilePurchaseFlow;
